import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Trash2, Minus, Plus, ShoppingBag, Tag, Gift, ArrowRight, Bookmark } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';
import { inr } from '@/lib/format';
import { useStore } from '@/context/StoreContext';
import { EmptyState } from '@/components/common/Loader';

export default function Cart() {
  const { cart, saved, updateQty, removeFromCart, saveForLater, moveToCart, removeSaved, cartSubtotal } = useStore();
  const navigate = useNavigate();
  const [coupon, setCoupon] = useState('');
  const [applied, setApplied] = useState(null);
  const [gift, setGift] = useState('');
  const [showGift, setShowGift] = useState(false);
  const [shipCfg, setShipCfg] = useState({ free_shipping_threshold: 1999, base_shipping: 99 });

  useEffect(() => {
    api.get('/checkout/config').then((r) => setShipCfg(r.data?.shipping || {})).catch(() => {});
  }, []);

  useEffect(() => { if (gift) localStorage.setItem('gaze_gift', gift); }, [gift]);

  const applyCoupon = async () => {
    if (!coupon.trim()) return;
    try {
      const { data } = await api.post('/coupons/validate', { code: coupon, subtotal: cartSubtotal });
      if (data.valid) { setApplied(data); localStorage.setItem('gaze_coupon', data.code); toast.success(data.message); }
      else { setApplied(null); toast.error(data.message); }
    } catch { toast.error('Could not validate coupon'); }
  };

  const discount = applied ? applied.discount : 0;
  const freeAt = Number(shipCfg.free_shipping_threshold ?? 1999);
  const baseShip = Number(shipCfg.base_shipping ?? 99);
  const shipping = freeAt <= 0 || cartSubtotal - discount >= freeAt || cartSubtotal === 0 ? 0 : baseShip;
  const total = Math.max(0, cartSubtotal - discount + shipping);

  const proceed = () => {
    if (gift) localStorage.setItem('gaze_gift', gift); else localStorage.removeItem('gaze_gift');
    if (applied) localStorage.setItem('gaze_coupon', applied.code); else localStorage.removeItem('gaze_coupon');
    navigate('/checkout');
  };

  if (cart.length === 0 && saved.length === 0) {
    return <div className="container-gaze"><EmptyState icon={ShoppingBag} title="Your cart is empty" subtitle="The Decor That Speaks: It&apos;s You. Discover pieces to make it yours." action={<Link to="/shop" className="mt-2 rounded-full bg-[hsl(var(--primary))] px-6 py-3 text-sm font-semibold text-white">Start Shopping</Link>} /></div>;
  }

  return (
    <div className="container-gaze py-8">
      <h1 className="text-3xl font-semibold">Shopping Cart</h1>
      <div className="mt-6 grid gap-8 lg:grid-cols-3">
        <div className="lg:col-span-2">
          <div className="space-y-4">
            {cart.map((it) => (
              <div key={it.key} data-testid="cart-line-item" className="flex gap-4 rounded-2xl border border-border bg-white p-4">
                <Link to={`/product/${it.slug}`} className="h-24 w-24 shrink-0 overflow-hidden rounded-xl"><img src={it.image} alt={it.name} className="h-full w-full object-cover" /></Link>
                <div className="min-w-0 flex-1">
                  <div className="flex justify-between gap-2">
                    <Link to={`/product/${it.slug}`} className="text-sm font-semibold hover:underline">{it.name}</Link>
                    <button onClick={() => removeFromCart(it.key)} aria-label="Remove"><Trash2 size={16} className="text-muted-foreground hover:text-destructive" /></button>
                  </div>
                  {it.variant && <p className="mt-0.5 text-xs text-muted-foreground">{Object.entries(it.variant).map(([k, v]) => `${k}: ${v}`).join(' · ')}</p>}
                  <div className="mt-3 flex items-center justify-between">
                    <div className="flex items-center rounded-full border border-border">
                      <button onClick={() => updateQty(it.key, it.qty - 1)} className="grid h-8 w-8 place-items-center"><Minus size={14} /></button>
                      <span className="w-8 text-center text-sm font-semibold">{it.qty}</span>
                      <button onClick={() => updateQty(it.key, it.qty + 1)} className="grid h-8 w-8 place-items-center"><Plus size={14} /></button>
                    </div>
                    <span className="text-sm font-bold">{inr(it.price * it.qty)}</span>
                  </div>
                  <button onClick={() => saveForLater(it.key)} className="mt-2 inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"><Bookmark size={13} /> Save for later</button>
                </div>
              </div>
            ))}
          </div>

          {saved.length > 0 && (
            <div className="mt-8">
              <h2 className="mb-3 text-lg font-semibold">Saved for later ({saved.length})</h2>
              <div className="space-y-3">
                {saved.map((it) => (
                  <div key={it.key} className="flex items-center gap-4 rounded-2xl border border-border bg-white p-3">
                    <img src={it.image} alt={it.name} className="h-16 w-16 rounded-lg object-cover" />
                    <div className="flex-1"><p className="text-sm font-medium">{it.name}</p><p className="text-sm font-semibold">{inr(it.price)}</p></div>
                    <button onClick={() => moveToCart(it.key)} className="rounded-full border border-[hsl(var(--primary))] px-4 py-1.5 text-xs font-semibold text-[hsl(var(--primary))]">Move to cart</button>
                    <button onClick={() => removeSaved(it.key)} aria-label="Remove saved"><Trash2 size={15} className="text-muted-foreground" /></button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Summary */}
        <div className="lg:col-span-1">
          <div className="sticky top-24 space-y-4">
            <div className="rounded-2xl border border-border bg-white p-5">
              <div className="flex gap-2">
                <div className="flex flex-1 items-center gap-2 rounded-full border border-border px-3">
                  <Tag size={15} className="text-muted-foreground" />
                  <input value={coupon} onChange={(e) => setCoupon(e.target.value.toUpperCase())} placeholder="Coupon code" data-testid="cart-coupon-input" className="w-full bg-transparent py-2.5 text-sm outline-none" />
                </div>
                <button onClick={applyCoupon} data-testid="cart-apply-coupon-button" className="rounded-full bg-[hsl(var(--primary))] px-4 text-sm font-semibold text-white">Apply</button>
              </div>
              {applied && <p className="mt-2 text-xs font-medium text-green-700">{applied.code} applied — saved {inr(applied.discount)}</p>}
              <p className="mt-2 text-[11px] text-muted-foreground">Try WELCOME10, GAZE500 or LUXE15</p>
            </div>

            <div className="rounded-2xl border border-border bg-white p-5">
              <button onClick={() => setShowGift(!showGift)} className="flex w-full items-center gap-2 text-sm font-medium"><Gift size={16} className="gaze-gold" /> Add a gift message</button>
              {showGift && <textarea value={gift} onChange={(e) => setGift(e.target.value)} rows={3} maxLength={200} placeholder="Write your message (optional)" data-testid="cart-gift-message-textarea" className="mt-3 w-full rounded-lg border border-border px-3 py-2 text-sm" />}
            </div>

            <div className="rounded-2xl border border-border bg-white p-5">
              <h3 className="mb-3 text-sm font-semibold">Order Summary</h3>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between"><span className="text-muted-foreground">Subtotal</span><span className="font-medium">{inr(cartSubtotal)}</span></div>
                {discount > 0 && <div className="flex justify-between text-green-700"><span>Discount</span><span>-{inr(discount)}</span></div>}
                <div className="flex justify-between"><span className="text-muted-foreground">Shipping</span><span className="font-medium">{shipping === 0 ? 'Free' : inr(shipping)}</span></div>
                <div className="mt-2 flex justify-between border-t border-border pt-3 text-base font-bold"><span>Total</span><span data-testid="cart-total">{inr(total)}</span></div>
              </div>
              <button onClick={proceed} disabled={cart.length === 0} data-testid="cart-checkout-button" className="mt-4 flex w-full items-center justify-center gap-2 rounded-full bg-[hsl(var(--primary))] py-3.5 text-sm font-semibold text-white disabled:opacity-50">Proceed to Checkout <ArrowRight size={16} /></button>
              {freeAt > 0 && <p className="mt-3 text-center text-xs text-muted-foreground">Free shipping on orders over {inr(freeAt)}</p>}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
